"use client";

import { useEffect, useState, Suspense } from "react";
import { Box, Button, Container, Heading, Input, Stack, Text, Spinner } from "@chakra-ui/react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import PremiumCard from "@/components/ui/PremiumCard";
import { toaster } from "@/components/ui/toaster";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";

export default function RegisterPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!data.ok) {
        setError(data.error ?? "حدث خطأ في التسجيل");
        return;
      }

      const emailSent = Boolean(data?.data?.emailSent);
      setSuccess(
        emailSent
          ? "تم إرسال رسالة إلى بريدك الإلكتروني. يرجى تأكيد بريدك الإلكتروني."
          : "تم إنشاء الحساب. إذا لم تصلك رسالة التفعيل، يمكنك طلب إعادة الإرسال."
      );
      toaster.success({
        title: "تم إرسال رسالة إلى بريدك الإلكتروني. يرجى تأكيد بريدك الإلكتروني.",
        description: emailSent
          ? "افحص البريد الوارد والرسائل غير المرغوبة (Spam)."
          : "تم إنشاء الحساب. إذا لم تصلك رسالة التفعيل، يمكنك طلب إعادة الإرسال.",
      });
      // Clear form
      const emailForRedirect = formData.email.trim();
      setFormData({ name: "", email: "", password: "" });
      if (emailForRedirect) {
        const redirectUrl = `/auth/verify-pending?email=${encodeURIComponent(emailForRedirect)}`;
        setTimeout(() => {
          router.push(redirectUrl);
        }, 900);
      }
    } catch {
      setError("حدث خطأ في الاتصال بالخادم");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const roleParam = searchParams.get("role");
    if (roleParam === "member") {
      router.replace("/auth/member-signup");
    }
  }, [router, searchParams]);

  return (
    <Box as="main" bg="background" minH="100vh" position="relative" overflow="hidden">
      {/* Decorative background elements */}
      <Box
        position="absolute"
        top="-10%"
        right="-5%"
        w="400px"
        h="400px"
        bg="linear-gradient(135deg, rgba(126,34,206,0.1), rgba(109,40,217,0.05))"
        borderRadius="50%"
        filter="blur(80px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        w="350px"
        h="350px"
        bg="linear-gradient(135deg, rgba(159,122,234,0.1), rgba(126,34,206,0.05))"
        borderRadius="50%"
        filter="blur(80px)"
        pointerEvents="none"
      />
      
      <Container maxW="lg" py={{ base: 12, md: 20 }} px={{ base: 6, md: 8 }} position="relative">
        <PremiumCard variant="elevated" p={{ base: 6, md: 8 }}>
          <form onSubmit={handleSubmit}>
            <Stack gap={6}>
              <Heading
                size="lg"
                color="text"
              >
                إنشاء حساب
              </Heading>
              <Text color="muted">
                أنشئ حسابك للوصول إلى الدورات، البرامج، والمجتمع التعليمي.
              </Text>

              {/* Google Sign-In */}
              <Suspense fallback={<Box h="44px" />}>
                <GoogleSignInButton
                  text="signup_with"
                  defaultRedirect="/student"
                />
              </Suspense>

              {/* Separator */}
              <Box display="flex" alignItems="center" gap={3}>
                <Box flex={1} h="1px" bg="border" />
                <Text color="muted" fontSize="sm">أو</Text>
                <Box flex={1} h="1px" bg="border" />
              </Box>
              
              {error && (
                <Box
                  bg="red.50"
                  border="1px solid"
                  borderColor="red.200"
                  borderRadius="md"
                  p={3}
                >
                  <Text color="red.600" fontSize="sm">
                    {error}
                  </Text>
                </Box>
              )}
              
              {success && (
                <Box
                  bg="green.50"
                  border="1px solid"
                  borderColor="green.200"
                  borderRadius="md"
                  p={3}
                >
                  <Text color="green.600" fontSize="sm">
                    {success}
                  </Text>
                </Box>
              )}
              
              <Stack gap={4}>
                <Box as="label">
                  <Text mb={2} fontWeight="600">
                    الاسم الكامل
                  </Text>
                  <Input
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="اسمك الكامل"
                    bg="background"
                    border="2px solid"
                    borderColor="border"
                    borderRadius="button"
                    px={4}
                    py={3}
                    w="100%"
                    transition="all 0.2s"
                    _hover={{ borderColor: "borderAccent" }}
                    _focus={{
                      outline: "none",
                      borderColor: "accent",
                      boxShadow: "0 0 0 1px var(--color-accent)"
                    }}
                    required
                    disabled={isLoading}
                  />
                </Box>
                <Box as="label">
                  <Text mb={2} fontWeight="600">
                    البريد الإلكتروني
                  </Text>
                  <Input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    bg="background"
                    border="2px solid"
                    borderColor="border"
                    borderRadius="button"
                    px={4}
                    py={3}
                    w="100%"
                    transition="all 0.2s"
                    _hover={{ borderColor: "borderAccent" }}
                    _focus={{
                      outline: "none",
                      borderColor: "accent",
                      boxShadow: "0 0 0 1px var(--color-accent)"
                    }}
                    required
                    disabled={isLoading}
                  />
                </Box>
                <Box as="label">
                  <Text mb={2} fontWeight="600">
                    كلمة المرور
                  </Text>
                  <Input
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    bg="background"
                    border="2px solid"
                    borderColor="border"
                    borderRadius="button"
                    px={4}
                    py={3}
                    w="100%"
                    transition="all 0.2s"
                    _hover={{ borderColor: "borderAccent" }}
                    _focus={{
                      outline: "none",
                      borderColor: "accent",
                      boxShadow: "0 0 0 1px var(--color-accent)"
                    }}
                    minLength={8}
                    required
                    disabled={isLoading}
                  />
                  <Text color="muted" fontSize="xs" mt={1}>
                    يجب أن تكون 8 أحرف على الأقل
                  </Text>
                </Box>
              </Stack>
              <Button
                type="submit"
                bg="primary"
                color="primaryText"
                _hover={{
                  bg: "primaryHover",
                  transform: "translateY(-1px)",
                  boxShadow: "lg"
                }}
                transition="all 0.2s"
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" color="spinner" /> : "إنشاء الحساب"}
              </Button>
              <Link href="/auth/member-signup" style={{ textDecoration: "none" }}>
                <Button
                  variant="outline"
                  borderColor="border"
                  color="text"
                  _hover={{ borderColor: "accent", color: "accent" }}
                  w="full"
                >
                  التسجيل كعضو
                </Button>
              </Link>
              <Text color="muted" fontSize="sm">
                لديك حساب بالفعل؟{" "}
                <Link href="/auth/login" style={{ textDecoration: "none" }}>
                  <Text
                    as="span"
                    color="link"
                    fontWeight="600"
                    _hover={{ textDecoration: "underline" }}
                  >
                    سجّل الدخول
                  </Text>
                </Link>
              </Text>
            </Stack>
          </form>
        </PremiumCard>
      </Container>
    </Box>
  );
}
